import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrgContext from '../context/OrgContext'; // Adjust the path as needed


const Articles = () => {
  const [articles, setArticles] = useState([]);  // Initialize as an empty array
  const [filteredArticles, setFilteredArticles] = useState([]); // Also initialize as an array
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newArticle, setNewArticle] = useState({ name: '', description: '', unit: '', price: 0, qty: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const itemsPerPage = 5; // Number of articles per page
  const { activeOrg } = useContext(OrgContext);

  const token = sessionStorage.getItem('token');

  // Fetch articles once when the token is available
  const fetchArticles = useCallback(async () => {
    if (activeOrg) {
      try {
        console.log("activeOrg:", activeOrg); // Debugging: Check if activeOrg is available
        const response = await axios.get(`${process.env.REACT_APP_SRV}/articles`, {
          headers: {
            Authorization: `Bearer ${token}`,
            orgid: `${activeOrg ? activeOrg._id : ''}`  // Include activeOrg in headers
          },
        });
        setArticles(response.data || []);
        setFilteredArticles(response.data || []);
      } catch (error) {
        console.error('Error fetching articles:', error.response ? error.response.data : error.message);
        toast.error('Error fetching articles.');
      }
    }
  }, [activeOrg, token]);  // Include 'token' as a dependency

  useEffect(() => {
    fetchArticles();  // Calling fetch from useEffect
  }, [activeOrg]);  // Add fetch to the dependency array

  // Filter articles based on search query
  useEffect(() => {
    const filtered = articles?.filter(article =>
      article.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredArticles(filtered || []); // Ensure filtered is an array
  }, [searchQuery, articles]);  // Re-run filter when search query or articles change

  const openDialog = (article = null) => {
    if (article) {
      setNewArticle({ ...article }); // Set article to edit
      setIsEditing(true); // Set editing mode
    } else {
      setNewArticle({ name: '', description: '', unit: '', price: 0, qty: 0 }); // Reset for new article
      setIsEditing(false); // Reset editing mode
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const showToast = (message) => {
    toast.success(message);  // Display success message using toast
  };

  const handleCreateOrUpdate = async () => {
    if (!newArticle.name || newArticle.price <= 0 || newArticle.qty <= 0) {
      toast.error('Please fill out all fields correctly.');
      return;
    }

    try {
      let response;
      if (newArticle && newArticle._id) {
        // Editing an existing article
        response = await axios.put(`${process.env.REACT_APP_SRV}/articles/${newArticle._id}`, newArticle, {
          headers: {
            Authorization: `Bearer ${token}`,
            orgid: `${activeOrg ? activeOrg._id : ''}` // Include JWT token in headers
            // Include JWT token in headers
          },
        });
      } else {
        // Creating a new article
        response = await axios.post(`${process.env.REACT_APP_SRV}/articles`, newArticle, {
          headers: {
            Authorization: `Bearer ${token}`,
            orgid: `${activeOrg ? activeOrg._id : ''}` // Include JWT token in headers
            // Include JWT token in headers
          },
        });
      }

      const { article, message } = response.data;

      if (article && article.name) {
        if (isEditing) {
          setArticles(articles.map(ing => ing._id === article._id ? article : ing));
        } else {
          setArticles([...articles, article]);
        }

        showToast(message); // Display message in toast
      } else {
        console.error('Invalid article data received:', response.data);
      }

      closeDialog();
      setNewArticle({ name: '', description: '', unit: '', price: 0, qty: 0 }); // Reset after operation
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} article:`, error.response ? error.response.data : error.message);
      toast.error(`Error ${isEditing ? 'updating' : 'creating'} article.`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_SRV}/articles/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          orgid: `${activeOrg ? activeOrg._id : ''}` // Include JWT token in headers
        },
      });
      const { message } = response.data;
      showToast(message); // Display message in toast
      setArticles(articles.filter(article => article._id !== id));
    } catch (error) {
      console.error('Error deleting article:', error.response ? error.response.data : error.message);
      if (error.status === 403){
        toast.warning("Not enough privileges");
      }else{
      toast.error('Error deleting article.');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Define these before currentItems
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Ensure that filtered is an array before slicing it
  const currentItems = Array.isArray(filteredArticles)
    ? filteredArticles.slice(indexOfFirstItem, indexOfLastItem)
    : [];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} /> {/* Toast messages container */}

      <div className="mb-4 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search articles..."
          value={searchQuery}
          onChange={handleSearch}
          className="px-4 py-2 bg-gray-800 rounded text-white"
        />
        <button onClick={() => openDialog()} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
          Add Article
        </button>
      </div>

      <ul>
        {currentItems.map((article) => (
          <li key={article._id} className="bg-gray-800 p-4 mb-2 rounded flex justify-between items-center">
            <div>
              <h3 className="text-xl">{article.name}</h3>
              <p className="text-gray-400">{article.description}</p>
              <p className="text-gray-400">Price: {article.price / article.qty} per {article.unit}, order quantity: {article.qty} {article.qty === 1 ? article.unit : article.unit + 's'} </p>
            </div>
            <div>
              <button
                onClick={() => openDialog(article)} // Pass article to edit
                className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700 mr-2"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(article._id)}
                className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Pagination */}
      <div className="flex justify-center mt-4">
        {Array.from({ length: Math.ceil(filteredArticles.length / itemsPerPage) }, (_, i) => (
          <button
            key={i}
            onClick={() => handlePageChange(i + 1)}
            className={`px-3 py-2 mx-1 rounded ${currentPage === i + 1 ? 'bg-golden' : 'bg-gray-800 hover:bg-gray-700'}`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {/* Add/Edit Article Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
          <div className="bg-gray-800 p-6 rounded-lg">
            <h3 className="text-xl mb-4">{isEditing ? 'Edit Article' : 'Add New Article'}</h3>

            <label className="text-white mb-1 block">Name</label>
            <input
              type="text"
              placeholder="Name"
              value={newArticle.name}
              onChange={(e) => setNewArticle({ ...newArticle, name: e.target.value })}
              className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
            />

            <label className="text-white mb-1 block">Description</label>
            <textarea
              placeholder="Description"
              value={newArticle.description}
              onChange={(e) => setNewArticle({ ...newArticle, description: e.target.value })}
              className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
            ></textarea>

            {/* Flex container for Order Price, Quantity, and Unit */}
            <div className="flex justify-between mb-4">
              <div className="flex-1 mr-2">
                <label className="text-white mb-1 block">Order Price</label>
                <input
                  type="number"
                  placeholder="Order Price"
                  value={newArticle.price !== undefined ? newArticle.price : ''}
                  onChange={(e) => setNewArticle({
                    ...newArticle, price: e.target.value
                  })}
                  className="block w-full p-2 bg-gray-700 text-white rounded"
                />
              </div>

              <div className="flex-1 mr-2">
                <label className="text-white mb-1 block">Order Quantity</label>
                <input
                  type="number"
                  placeholder="Order Quantity"
                  value={newArticle.qty !== undefined ? newArticle.qty : ''}
                  onChange={(e) => setNewArticle({ ...newArticle, qty: e.target.value })}
                  className="block w-full p-2 bg-gray-700 text-white rounded"
                />
              </div>

              <div className="flex-1">
                <label className="text-white mb-1 block">Unit (single)</label>
                <input
                  type="text"
                  placeholder="Unit (e.g., piece, kg)"
                  value={newArticle.unit}
                  onChange={(e) => setNewArticle({ ...newArticle, unit: e.target.value })}
                  className="block w-full p-2 bg-gray-700 text-white rounded"
                />
              </div>
            </div>

            <button onClick={handleCreateOrUpdate} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
              {isEditing ? 'Update Article' : 'Create Article'}
            </button>
            <button onClick={closeDialog} className="bg-red-600 px-4 py-2 ml-2 rounded hover:bg-red-700">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articles;
